// How one pattern moved over time.
//
// The server does the replay, because the rules being replayed are the server's own and a second
// copy of the promotion ladder living in the browser would drift the first time either was tuned.
// What happens here is what always happens here: shape the rows, open the ciphertext.

import { ApiError, apiGet } from './client';
import { openCipher } from './cipher';

export type Trajectory = 'rising' | 'stable' | 'fading';

export interface TimelineMonth {
  /** "YYYY-MM". */
  month: string;
  /** Sightings inside this month. */
  count: number;
  /** Sightings still counting toward promotion at month end. */
  inWindow: number;
  significance: string;
  trajectory: Trajectory;
  lifecycle: string;
}

export interface QuietSpell {
  from: string;
  /** Null while the silence is still going. */
  to: string | null;
  days: number;
}

export interface TimelineSighting {
  entryId: string | null;
  at: string;
  /** dream | waking | chat | onboarding. */
  source: string;
  /** The snippet that carried it, decrypted. Empty when none was stored. */
  context: string;
}

export interface TimelineCheckIn {
  at: string;
  reason: string;
  response: string;
  status: string;
}

export interface PatternTimeline {
  canonicalId: string;
  entityType: string;
  firstSeen: string | null;
  lastSeen: string | null;
  /** Entry-distinct, matching the count shown everywhere else. */
  total: number;
  dreamCount: number;
  wakingCount: number;
  months: TimelineMonth[];
  quiet: QuietSpell[];
  sightings: TimelineSighting[];
  checkIns: TimelineCheckIn[];
}

interface ArcResponse {
  firstSeen?: string | null;
  lastSeen?: string | null;
  total?: number | null;
  dreamCount?: number | null;
  wakingCount?: number | null;
  months?: Partial<TimelineMonth>[] | null;
  quiet?: Partial<QuietSpell>[] | null;
}

interface TimelineResponse {
  canonicalId?: string | null;
  entityType?: string | null;
  arc?: ArcResponse | null;
  sightings?: {
    source_entry_id?: string | null;
    detected_at?: string | null;
    source_type?: string | null;
    context_cipher?: string | null;
  }[] | null;
  checkIns?: Partial<TimelineCheckIn>[] | null;
}

const TRAJECTORIES: Trajectory[] = ['rising', 'stable', 'fading'];

function trajectory(raw: unknown): Trajectory {
  return TRAJECTORIES.includes(raw as Trajectory) ? (raw as Trajectory) : 'stable';
}

/**
 * Shape a month row, defending each field separately.
 *
 * A month missing its key is dropped rather than defaulted, because a row labelled with the wrong
 * month is worse than one absent: the chart would still draw, and would be quietly wrong.
 */
function toMonth(row: Partial<TimelineMonth>): TimelineMonth[] {
  if (!row.month) return [];
  return [{
    month: row.month,
    count: Number(row.count ?? 0),
    inWindow: Number(row.inWindow ?? 0),
    significance: row.significance ?? 'emerging',
    trajectory: trajectory(row.trajectory),
    lifecycle: row.lifecycle ?? 'new_signal',
  }];
}

/**
 * Fetch the arc of one pattern. Returns null when the server has never heard of it, which is a
 * real answer for a symbol the user typed by hand that extraction has not corroborated.
 */
export async function fetchPatternTimeline(
  dek: CryptoKey,
  canonicalId: string,
  signal?: AbortSignal,
): Promise<PatternTimeline | null> {
  let res: TimelineResponse;
  try {
    res = await apiGet<TimelineResponse>(
      `/api/patterns/timeline?canonical_id=${encodeURIComponent(canonicalId)}`,
      signal,
    );
  } catch (err) {
    // A pattern with no evidence answers 404, and a worker deployed before this route existed
    // answers 404 too. Both mean the same thing to the screen: there is no arc to draw, which is
    // not a failure worth an error state. Every other status still throws.
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }

  const arc = res.arc ?? {};
  const sightings = await Promise.all(
    (res.sightings ?? []).map(async (row) => ({
      entryId: row.source_entry_id ?? null,
      at: row.detected_at ?? '',
      source: row.source_type ?? 'waking',
      context: await openCipher(dek, row.context_cipher),
    })),
  );

  return {
    canonicalId: res.canonicalId ?? canonicalId,
    entityType: res.entityType ?? 'symbol',
    firstSeen: arc.firstSeen ?? null,
    lastSeen: arc.lastSeen ?? null,
    total: Number(arc.total ?? 0),
    dreamCount: Number(arc.dreamCount ?? 0),
    wakingCount: Number(arc.wakingCount ?? 0),
    months: (arc.months ?? []).flatMap(toMonth),
    quiet: (arc.quiet ?? []).flatMap((q) =>
      q.from ? [{ from: q.from, to: q.to ?? null, days: Number(q.days ?? 0) }] : [],
    ),
    sightings: sightings.filter((s) => s.at),
    checkIns: (res.checkIns ?? []).flatMap((c) =>
      c.at ? [{ at: c.at, reason: c.reason ?? '', response: c.response ?? '', status: c.status ?? '' }] : [],
    ),
  };
}

/**
 * Plain-language summary of an arc, for the line above the chart.
 *
 * Written rather than templated from the raw numbers because the interesting sentence differs by
 * shape: a pattern that stopped wants its silence named, a rising one wants its direction named,
 * and one seen twice wants neither — it wants to be told it is early.
 */
export function arcSummary(timeline: PatternTimeline): string {
  const { months, quiet, total } = timeline;
  if (total === 0) return 'No sightings yet.';

  const latest = months[months.length - 1];
  const open = quiet.find((q) => q.to === null);

  if (open && open.days >= 30) {
    const span = open.days >= 60 ? `${Math.round(open.days / 30)} months` : `${open.days} days`;
    return `Quiet for ${span}. It was a real thread before that, and it is kept rather than dropped.`;
  }
  if (latest?.trajectory === 'rising') return 'Coming up more lately than it was.';
  if (latest?.trajectory === 'fading') return 'Winding down — less present than it was a month ago.';
  if (total <= 2) return 'Early. Too few sightings to call it a direction yet.';

  const returns = quiet.filter((q) => q.to !== null).length;
  if (returns > 0) return `Comes and goes — it has gone quiet and returned ${returns === 1 ? 'once' : `${returns} times`}.`;
  return 'Steady. Turning up at about the rate it has been.';
}
